import {
  Close,
  Home,
  ManageAccounts,
  Search,
  Timelapse,
  TrendingUp,
  Menu,
  Campaign,
} from '@mui/icons-material';

import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  Modal,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import PrimaryNavButton from '../desktop/primary-nav-button';
import { useUserStore } from '@oai-frontend-monorepo/state-management';
import FullTextLogo from '../../icons/logo/full-text-logo';
import NavUnreadCountComponent from '../../../features/notifications/nav-unread-count.component';
import { useLocation, useParams } from 'react-router-dom';
import Link from '../../navigation/Link';
import { queryClient } from '@oai-frontend-monorepo/api';
import PaperAccountHeader from '../../../features/subscribers/components/paper-account-header.component';
import { oktaAuth } from '../../../libs/okta';
import QuestionModalContent from '../../../features/user-question/question-modal-content';
import React from 'react';

interface Props {
  children: React.ReactNode;
}

const MobileLayout = ({ children }: Props) => {
  const [open, setOpen] = useState(false);

  const store = useUserStore();

  const handleSignOut = async () => {
    queryClient.clear();
    store.setToken(undefined);
    oktaAuth.clearStorage();
    oktaAuth.closeSession();
  };

  const toggleDrawer =
    (open: boolean) => (_: React.KeyboardEvent | React.MouseEvent) => {
      setOpen(open);
    };

  const [questionModalOpen, setQuestionModalOpen] = React.useState(false);

  const handleQuestion = () => {
    setQuestionModalOpen(true);
  };

  // todo: possibly move this to its own component
  // if it remains shared between mobile and desktop
  const buttons = useMemo(() => {
    const initialValues = [
      {
        title: 'Dashboard',
        icon: Home,
        link: '/dashboard',
      },
      {
        title: 'Trade',
        icon: Search,
        link: '/trade',
      },
      {
        title: 'Positions',
        icon: TrendingUp,
        link: '/positions',
      },
      {
        title: 'Orders',
        icon: Timelapse,
        link: '/orders',
      },
      {
        title: 'Price Alerts',
        icon: Campaign,
        link: '/price-alerts',
      },
      {
        title: 'Notifications',
        icon: NavUnreadCountComponent,
        link: '/notifications',
      },
      {
        title: 'Account',
        icon: ManageAccounts,
        link: '/account',
      },
    ];

    return initialValues;
  }, []);

  const location = useLocation();
  const params = useParams();

  const currentPath = location?.pathname?.split('/')?.[1] || '';

  if (
    (currentPath === 'trade' && params?.symbol) ||
    (currentPath === 'positions' && params?.id)
  ) {
    return children;
  }

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        position="sticky"
        top={0}
        bgcolor="white"
        zIndex={2}
        alignItems="flex-start"
        paddingRight={2}
      >
        <Box display={'flex'} flexDirection={'column'} p={2}>
          <Link to="/dashboard">
            <FullTextLogo />
          </Link>

          <PaperAccountHeader mt={3} ml={0.3} />
        </Box>

        <IconButton sx={{ pt: 2 }} color="primary" onClick={toggleDrawer(true)}>
          <Menu />
        </IconButton>
      </Box>

      <Drawer
        open={open}
        // onOpen={toggleDrawer(false)}
        onClose={toggleDrawer(false)}
      >
        <Box display="flex" flexDirection="column" width="100vw">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            pr={2}
          >
            <Box p={2}>
              <FullTextLogo />
            </Box>

            <IconButton color="primary" onClick={toggleDrawer(false)}>
              <Close />
            </IconButton>
          </Box>

          <List>
            {buttons.map((button) => (
              <PrimaryNavButton
                onSelect={toggleDrawer(false)}
                active={currentPath === button.link.split('/')[1]}
                key={button.title}
                title={button.title}
                Icon={button.icon}
                isExpanded={open}
                link={button.link}
              />
            ))}
          </List>

          <Box onClick={handleQuestion} mt={2} mx={2}>
            <Box
              sx={{
                cursor: 'pointer',
              }}
              display="flex"
              alignItems="center"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.9502 0C16.1868 0.000105393 17.9999 1.81321 18 4.0498V13.9502C17.9999 16.1868 16.1868 17.9999 13.9502 18H4.0498C1.81321 17.9999 0.000105393 16.1868 0 13.9502V4.0498C0.00010578 1.81321 1.81321 0.000105587 4.0498 0H13.9502ZM8.90332 12.8984C8.63435 12.8985 8.37578 13.0059 8.18555 13.1963C7.99541 13.3866 7.88867 13.6451 7.88867 13.9141C7.88876 14.1829 7.99563 14.4407 8.18555 14.6309C8.37576 14.8211 8.63435 14.9286 8.90332 14.9287C9.17239 14.9287 9.4308 14.8211 9.62109 14.6309C9.81119 14.4407 9.91788 14.1831 9.91797 13.9141C9.91797 13.645 9.81138 13.3866 9.62109 13.1963C9.43082 13.0059 9.17238 12.8984 8.90332 12.8984ZM8.88281 3.27246C8.23711 3.27609 7.60557 3.46244 7.06055 3.80859C6.51566 4.15512 5.80176 5.23047 5.80176 5.23047C5.80013 5.23687 5.67009 5.75079 5.75781 6.00195C5.84597 6.2546 6.03125 6.4621 6.27246 6.57812H6.25781C6.49879 6.69454 6.77662 6.71032 7.0293 6.62207C7.28195 6.53391 7.4893 6.34863 7.60547 6.10742C7.7209 5.86416 7.90215 5.65796 8.12891 5.5127C8.35602 5.3674 8.6201 5.289 8.88965 5.28613C9.26406 5.29097 9.62284 5.43843 9.89258 5.69824C10.1621 5.95809 10.323 6.3107 10.3418 6.68457C10.3515 6.99265 10.2618 7.29647 10.0859 7.5498C9.91035 7.80291 9.65784 7.99278 9.36621 8.0918C8.94194 8.2305 8.57144 8.4993 8.30762 8.85938C8.04399 9.21921 7.89916 9.65261 7.89453 10.0986V10.9043C7.89459 11.2648 8.08726 11.5979 8.39941 11.7783C8.71177 11.9587 9.09684 11.9587 9.40918 11.7783C9.72151 11.5979 9.91401 11.2649 9.91406 10.9043V10.0986C9.9143 10.0409 10.0091 10.0068 10.0146 10.0049C10.7153 9.76962 11.322 9.31474 11.7441 8.70801C12.1664 8.10138 12.3814 7.37445 12.3584 6.63574C12.3252 5.73845 11.9482 4.88789 11.3047 4.26172C10.6613 3.63564 9.80098 3.28137 8.90332 3.27246H8.88281Z"
                  fill="#FFC700"
                />
              </svg>

              <Typography
                sx={{
                  ml: 1,
                  color: '#FFC700',
                  fontWeight: 900,
                  fontSize: 15.5,
                }}
              >
                Ask The Team
              </Typography>
            </Box>
            <Typography
              fontSize={12}
              mt={0.5}
              color={'text.secondary'}
              sx={{
                textOverflow: 'ellipsis',
              }}
            >
              Your trading questions answered
            </Typography>
          </Box>

          {/* <Box mx={2}>
            <Button variant="outlined" onClick={handleSignOut}>
              Sign Out
            </Button>
          </Box> */}
        </Box>
      </Drawer>

      <Box mt={4}>
        {children}

        <Modal
          open={questionModalOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClose={() => {
            return setQuestionModalOpen(false);
          }}
        >
          <QuestionModalContent onClose={() => setQuestionModalOpen(false)} />
        </Modal>
      </Box>
    </>
  );
};

export default MobileLayout;
